import React, { useState, useEffect } from 'react';
import { API, getToken } from './adminApi';

const SubmissionsTab = () => {
  const [submissions, setSubmissions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [fetchError, setFetchError] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [rejectNotes, setRejectNotes] = useState({});
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { fetchSubmissions(); }, [statusFilter]);

  const fetchSubmissions = async () => {
    setFetchError('');
    try {
      const token = await getToken();
      const res = await fetch(`${API}/admin/submissions?status=${statusFilter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSubmissions(data.submissions || []);
    } catch (err) {
      setFetchError(err.message);
    }
  };

  const approve = async (id) => {
    const token = await getToken();
    const res = await fetch(`${API}/admin/submissions/${id}/approve`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    });
    const data = await res.json();
    setActionMsg(res.ok ? `✓ Approved and published as /${data.databaseRef}` : `Error: ${data.error}`);
    fetchSubmissions();
  };

  const reject = async (id) => {
    const token = await getToken();
    const res = await fetch(`${API}/admin/submissions/${id}/reject`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ notes: rejectNotes[id] || '' })
    });
    const data = await res.json();
    setActionMsg(res.ok ? '✓ Rejected' : `Error: ${data.error}`);
    fetchSubmissions();
  };

  return (
    <div>
      <div className="as-filter-tabs" style={{ marginBottom: '1rem' }}>
        {['pending', 'approved', 'rejected'].map(s => (
          <button key={s} className={`as-filter-tab ${statusFilter === s ? 'as-filter-tab--active' : ''}`} onClick={() => setStatusFilter(s)}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {actionMsg && <div className="as-action-msg" onClick={() => setActionMsg('')}>{actionMsg}</div>}
      {fetchError && <div className="as-error">{fetchError}</div>}

      {submissions.length === 0 ? (
        <div className="as-empty">No {statusFilter} submissions.</div>
      ) : (
        <div className="as-list">
          {submissions.map(s => (
            <div key={s._id} className={`as-card ${expanded === s._id ? 'as-card--open' : ''}`}>
              <div className="as-card-header" onClick={() => setExpanded(expanded === s._id ? null : s._id)}>
                <div className="as-card-info">
                  <span className="as-card-title">{s.title}</span>
                  <span className="as-card-meta">{s.location} · {new Date(s.createdAt).toLocaleDateString('en-GB')}</span>
                  <span className="as-card-meta">{s.submittedBy} · {s.submittedByEmail}</span>
                </div>
                <div className="as-card-status as-card-status--${s.status}">{s.status}</div>
                <span className="as-chevron">{expanded === s._id ? '▲' : '▼'}</span>
              </div>

              {expanded === s._id && (
                <div className="as-card-body">
                  {s.pics?.length > 0 && (
                    <div className="as-images">
                      {s.pics.map((pic, i) => (
                        <img key={i} src={pic} alt="" className="as-thumb" />
                      ))}
                    </div>
                  )}

                  <div className="as-fields">
                    {s.homePageTagline && <p><strong>Tagline:</strong> {s.homePageTagline}</p>}
                    {s.description && <p><strong>Description:</strong> {s.description}</p>}
                    {s.tags && <p><strong>Tags:</strong> {s.tags}</p>}
                    {s.location && <p><strong>Location:</strong> {s.location}</p>}
                    {s.submissionReason && <p><strong>Notes from submitter:</strong> {s.submissionReason}</p>}
                    <p><strong>DB ref:</strong> /{s.databaseRef}</p>
                  </div>

                  {s.status === 'pending' && (
                    <div className="as-actions">
                      <button className="as-btn as-btn--approve" onClick={() => approve(s._id)}>
                        ✓ Approve &amp; Publish
                      </button>
                      <div className="as-reject-row">
                        <input
                          className="as-reject-notes"
                          placeholder="Optional rejection notes..."
                          value={rejectNotes[s._id] || ''}
                          onChange={e => setRejectNotes(prev => ({ ...prev, [s._id]: e.target.value }))}
                        />
                        <button className="as-btn as-btn--reject" onClick={() => reject(s._id)}>
                          ✕ Reject
                        </button>
                      </div>
                    </div>
                  )}

                  {s.status === 'approved' && (
                    <p className="as-reviewed-note">Approved by {s.reviewedBy} on {new Date(s.reviewedAt).toLocaleDateString('en-GB')}</p>
                  )}
                  {s.status === 'rejected' && (
                    <p className="as-reviewed-note">Rejected by {s.reviewedBy} on {new Date(s.reviewedAt).toLocaleDateString('en-GB')}{s.reviewNotes ? ` — "${s.reviewNotes}"` : ''}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SubmissionsTab;
